import { ENABLE_STANDARDS_OUTLINE } from "Constants/permissionConstants";
import React from "react";
import { Trans } from "react-i18next";
import ACLStore from "lib/aclStore";

export const POWER_STANDARD_HEADER_PREFIX = {
  type: "avatar",
  attrs: {
    name: "P",
    color: "neutral",
  },
};

export const POWER_STANDARD_CELL_PREFIX = {
  type: "avatar",
  name: "P",
  color: "black",
  size: 20,
};

export const DATA_GRID_CHECKBOX_STATES = {
  CHECKED: "checked",
  UNCHECKED: "unchecked",
  INDETERMINATE: "indeterminate",
};

export const GRADING_TYPE_TOOLTIP = (
  <div
    style={{
      padding: "8px 12px",
      color: "white",
      font: "500 12px AvenirNext",
      backgroundColor: "rgb(0, 0, 0)",
      borderRadius: 9,
      wordWrap: "break-word",
      width: "300px",
    }}
  >
    <Trans
      i18nKey={"scopeAndSequence:grading_type_header_tooltip"}
      components={{
        1: <ul style={{ margin: 0, padding: "0 0 0 12px" }} />,
        2: <li style={{ margin: 0, padding: 0 }} />,
      }}
    />
  </div>
);

export const GRADING_TYPES = {
  VERTICAL: "VERTICAL",
  INDIVIDUAL: "INDIVIDUAL",
  NONE: "NONE",
};

export const GRADING_TYPE_TO_LABEL_MAP = t => ({
  [GRADING_TYPES.VERTICAL]: t("scopeAndSequence:vertical_rollup"),
  [GRADING_TYPES.INDIVIDUAL]: t("common:indi"),
  [GRADING_TYPES.NONE]: t("common:na"),
});

export const DATA_GRID_COLUMN_TYPES = {
  SELECTION: "SELECTION",
  STATUS: "STATUS",
  DEPTH_ADJUSTER: "DEPTH_ADJUSTER",
  CODE: "CODE",
  TITLE: "TITLE",
  TITLE_TRANSLATED: "TITLE_TRANSLATED",
  ID: "ID",
  LIST_PARENT: "LIST_PARENT",
  GRADED: "GRADED",
  POWER_STANDARD: "POWER_STANDARD",
  GRADING_TYPE: "GRADING_TYPE",
  WEIGHT: "WEIGHT",
  GRADE_LEVELS: "GRADE_LEVELS",
  SUBJECT_AREAS: "SUBJECT_AREAS",
  TAGS: "TAGS",
  TAGS_TRANSLATED: "TAGS_TRANSLATED",
  DESCRIPTION: "DESCRIPTION",
  DESCRIPTION_TRANSLATED: "DESCRIPTION_TRANSLATED",
  TAGGED_IN_COURSES: "TAGGED_IN_COURSES",
  TAGGED_IN_ASSIGNMENTS: "TAGGED_IN_ASSIGNMENTS",
  TAGGED_IN_PORTFOLIO: "TAGGED_IN_PORTFOLIO",
  TAGGED_IN_UNITS: "TAGGED_IN_UNITS",
  LAST_UPDATED_BY: "LAST_UPDATED_BY",
  ARCHIVE: "ARCHIVE",
  DELETE: "DELETE",
  STAAR_QUES: "STAAR_QUES",
  LOCK: "LOCK",
  SUBJECT_GROUP: "SUBJECT_GROUP",
  MEDIA: "MEDIA",
};

export const DATA_GRID_CELL_TYPES = {
  TEXT: "text",
  DROPDOWN: "dropdown",
  CHECKBOX: "checkbox",
  ICON: "icon",
  LABEL: "label",
  TAG: "tag",
  ROW_COLLAPSE: "rowCollapse",
  NUMBER: "number",
  SKELETON: "skeleton",
  ICON_PICKER: "iconPicker",
};

export const EMPTY_CELL = {
  cellType: DATA_GRID_CELL_TYPES.TEXT,
  value: "-",
};

export const EMPTY_CELL_WITH_CENTERED_DASH = {
  cellType: DATA_GRID_CELL_TYPES.TEXT,
  value: "-",
  styles: { align: "center" },
};

export const DEFAULT_STANDARDS_DATA_GRID_PROPERTIES = ({ t }) => ({
  [DATA_GRID_COLUMN_TYPES.SELECTION]: {
    headerConfig: {
      title: "",
      width: 40,
      toggleAllCheckboxesOnHeaderClick: true,
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.CHECKBOX,
      isEditable: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.STATUS]: {
    headerConfig: {
      width: 56,
      title: t("common:status"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.ICON,
    },
    customConfig: {
      property: "status",
    },
  },
  [DATA_GRID_COLUMN_TYPES.LOCK]: {
    headerConfig: {
      title: "",
      width: 40,
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.ICON,
      isEditable: false,
      tooltip: t("course:standard_has_been_locked_by_blueprint"),
    },

    customConfig: {
      property: "lockingInfo.isLocked",
      showEmptyCellAsFallback: true,
      affirmativeIcon: "LockFilled",
      negativeIcon: "LockOpenOutlined",
      dependentProperties: [],
      negativeIconTooltip: t(
        "course:standard_has_not_been_locked_by_blueprint"
      ),
    },
  },
  [DATA_GRID_COLUMN_TYPES.DEPTH_ADJUSTER]: {
    headerConfig: {
      width: 72,
      title: "",
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.ICON,
    },
  },
  [DATA_GRID_COLUMN_TYPES.CODE]: {
    headerConfig: {
      title: t("common:code"),
      width: 140,
      tooltip: t("scopeAndSequence:code_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.TEXT,
      placeholder: t("common:enter_with_label", {
        label: t("common:code"),
      }),
    },
    customConfig: {
      property: "code",
      showEmptyCellAsFallback: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.MEDIA]: {
    headerConfig: {
      title: t("scopeAndSequence:icon_image_emoji"),
      width: 244,
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.ICON_PICKER,
      pickerProps: {
        tabs: ["icon", "emoji", "image"],
        allowAcronym: false,
        allowCustomIconUpload: false,
        placement: "bottom left",
      },
    },
    customConfig: {
      property: "icon",
    },
  },
  [DATA_GRID_COLUMN_TYPES.TITLE]: {
    headerConfig: {
      title: t("common:title"),
      width: 640,
      tooltip: ACLStore.can(ENABLE_STANDARDS_OUTLINE)
        ? t("scopeAndSequence:title_header_tooltip")
        : t("scopeAndSequence:title_header_with_expand_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.ROW_COLLAPSE,
      placeholder: t("common:enter_with_label", {
        label: t("common:title"),
      }),
      isMultiLine: true,
    },
    customConfig: {
      property: "label",
      showEmptyCellAsFallback: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.ID]: {
    headerConfig: {
      title: t("scopeAndSequence:system_id"),
      width: 120,
      tooltip: t("scopeAndSequence:system_id_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.LABEL,
      styles: {
        backgroundColor: "#FAFAFA",
      },
    },
    customConfig: {
      property: "uid",
    },
  },
  [DATA_GRID_COLUMN_TYPES.LIST_PARENT]: {
    headerConfig: {
      title: t("scopeAndSequence:parent_id"),
      width: 120,
      tooltip: t("scopeAndSequence:parent_id_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.LABEL,
      styles: {
        backgroundColor: "#FAFAFA",
      },
    },
    customConfig: {
      property: "parentId",
    },
  },
  [DATA_GRID_COLUMN_TYPES.POWER_STANDARD]: {
    headerConfig: {
      title: "",
      width: 44,
      prefix: POWER_STANDARD_HEADER_PREFIX,
      tooltip: t("scopeAndSequence:power_standard_header_tooltip"),
      titleForConfigureColumns: t("scopeAndSequence:power_standard"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.LABEL,
    },
    customConfig: {
      property: "standardsWrapper.properties.isPowerStandard",
      prefix: [POWER_STANDARD_CELL_PREFIX],
    },
  },
  [DATA_GRID_COLUMN_TYPES.GRADED]: {
    headerConfig: {
      title: t("scopeAndSequence:graded"),
      width: 88,
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.ICON,
    },
    customConfig: {
      property: "standardsWrapper.properties.isGradedStandard",
      affirmativeIcon: "TickSmallOutlinedGreen",
      negativeIcon: "",
      showEmptyCellAsFallback: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.GRADING_TYPE]: {
    headerConfig: {
      title: t("common:grading"),
      width: 200,
      tooltip: GRADING_TYPE_TOOLTIP,
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.TEXT,
    },
    customConfig: {
      property: "gradingType",
      labelMap: GRADING_TYPE_TO_LABEL_MAP(t),
    },
  },
  [DATA_GRID_COLUMN_TYPES.WEIGHT]: {
    headerConfig: {
      title: t("common:weight"),
      width: 92,
      tooltip: t("scopeAndSequence:weight_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.NUMBER,
      styles: {
        align: "right",
      },
    },
    customConfig: {
      property: "standardsWrapper.properties.weight",
    },
  },
  [DATA_GRID_COLUMN_TYPES.GRADE_LEVELS]: {
    headerConfig: {
      title: t("scopeAndSequence:grade_levels"),
      width: 244,
      tooltip: t("scopeAndSequence:grade_levels_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.DROPDOWN,
      showDropdownIcon: false,
      isMulti: true,
      addNewOptionEnabled: false,
      placeholder: t("common:select_with_label", {
        label: t("scopeAndSequence:grade_levels"),
      }),
    },
    customConfig: {
      property: "standardsWrapper.properties.grades",
      showEmptyCellAsFallback: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.SUBJECT_AREAS]: {
    headerConfig: {
      title: t("scopeAndSequence:subject_areas"),
      width: 224,
      tooltip: t("scopeAndSequence:subject_area_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.DROPDOWN,
      showDropdownIcon: false,
      isMulti: true,
      addNewOptionEnabled: false,
      placeholder: t("common:select_with_label", {
        label: t("scopeAndSequence:subject_areas"),
      }),
    },
    customConfig: {
      property: "standardsWrapper.properties.subjects",
      showEmptyCellAsFallback: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.TAGS]: {
    headerConfig: {
      title: t("common:tags"),
      width: 224,
      tooltip: t("scopeAndSequence:tags_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.TAG,
      showDropdownIcon: false,
      isMulti: true,
      placeholder: t("common:select_with_label", {
        label: t("common:tags"),
      }),
    },
    customConfig: {
      property: "standardsWrapper.properties.tags",
      showEmptyCellAsFallback: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.DESCRIPTION]: {
    headerConfig: {
      title: t("common:description"),
      width: 610,
      tooltip: t("scopeAndSequence:description_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.TEXT,
      placeholder: t("common:enter_with_label", {
        label: t("common:description"),
      }),
      isMultiLine: true,
    },
    customConfig: { property: "subText", showEmptyCellAsFallback: true },
  },
  [DATA_GRID_COLUMN_TYPES.TAGGED_IN_COURSES]: {
    headerConfig: {
      title: t("scopeAndSequence:tagged_in_courses"),
      width: 144,
      tooltip: t("scopeAndSequence:tagged_in_courses_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.NUMBER,
      styles: {
        align: "right",
        color: "#008392",
      },
    },
    customConfig: {
      property: "standardCountWrapper.course.totalCount",
      showLoader: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.TAGGED_IN_ASSIGNMENTS]: {
    headerConfig: {
      title: t("scopeAndSequence:tagged_in_label", {
        label: t("common:task_plural"),
      }),
      width: 176,
      tooltip: t("scopeAndSequence:tagged_in_label_header_tooltip", {
        label: t("common:task_plural"),
      }),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.NUMBER,
      styles: {
        align: "right",
        color: "#008392",
      },
    },
    customConfig: {
      property: "standardCountWrapper.assignment.totalCount",
      showLoader: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.TAGGED_IN_PORTFOLIO]: {
    headerConfig: {
      title: t("scopeAndSequence:tagged_in_portfolio"),
      width: 152,
      tooltip: t("scopeAndSequence:tagged_in_portfolio_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.NUMBER,
      styles: {
        align: "right",
        color: "#008392",
      },
    },
    customConfig: {
      property: "standardCountWrapper.portfolio.totalCount",
      showLoader: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.TAGGED_IN_UNITS]: {
    headerConfig: {
      title: t("scopeAndSequence:tagged_in_units"),
      width: 124,
      tooltip: t("scopeAndSequence:tagged_in_units_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.NUMBER,
      styles: {
        align: "right",
        color: "#008392",
      },
    },
    customConfig: {
      property: "standardCountWrapper.unitPlan.totalCount",
      showLoader: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.STAAR_QUES]: {
    headerConfig: {
      title: t("academicSetup:benchmarks_question_label"),
      width: 132,
      tooltip: t("scopeAndSequence:staar_questions_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.NUMBER,
      styles: {
        align: "right",
      },
    },
    customConfig: {
      property: "benchmarkQuestionCount.count",
    },
  },
  [DATA_GRID_COLUMN_TYPES.LAST_UPDATED_BY]: {
    headerConfig: {
      title: t("scopeAndSequence:last_updated_by"),
      width: 244,
      tooltip: t("scopeAndSequence:last_updated_by_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.LABEL,
    },
  },
  [DATA_GRID_COLUMN_TYPES.ARCHIVE]: {
    headerConfig: {
      title: t("common:archived"),
      width: 84,
      tooltip: t("scopeAndSequence:archive_header_tooltip"),
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.ICON,
    },
    customConfig: {
      property: "standardsWrapper.properties.isArchived",
      affirmativeIcon: "TickSmallOutlinedGreen",
      showEmptyCellAsFallback: true,
    },
  },
  [DATA_GRID_COLUMN_TYPES.DELETE]: {
    headerConfig: {
      title: "",
      width: 60,
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.ICON,
      value: "DeleteOutlined",
    },
  },
  [DATA_GRID_COLUMN_TYPES.SUBJECT_GROUP]: {
    headerConfig: {
      title: t("common:subject_group_plural"),
      width: 324,
    },
    cellConfig: {
      cellType: DATA_GRID_CELL_TYPES.DROPDOWN,
      showDropdownIcon: false,
      isMulti: true,
      placeholder: t("common:select_with_label", {
        label: t("common:subject_group_plural"),
      }),
    },
    customConfig: {
      property: "associatedParents",
      showEmptyCellAsFallback: true,
    },
  },
});
